import { getFullName, sortedBoard } from "@/utils/Roles";
import { autorun, makeAutoObservable, runInAction } from "mobx";
import {
  CleanupFunction,
  CompanyRoleIdWithName,
  DbotEvent,
  DbotEventDispatcher,
  DecisionItem,
  DecisionTextModel,
  DetailsPanel,
  DocumentContext,
  DocumentItem,
  isLanguageChanged,
  mapDecisionTextModel,
  mapDocumentLanguageToLocales,
  mapLocalesToDocumentLanguage,
  mapLocalizedDecisionTextsToModel,
  ProjectDocumentStatus,
  shareholdersWithShares,
  WithInitialization,
} from "@/dbot/common/commonDomain";
import {
  createElectronicParticipationField,
  defaultGeneralMeetingDates,
  GeneralMeetingElectronicParticipation,
  GeneralMeetingForm,
  GeneralMeetingType,
  getChairperson,
  isTooShortNotice,
  NoticeToGeneralMeeting,
  PowerOfAttorneyLanguage,
} from "@/dbot/common/commonGeneralMeetingDomain";
import { FileUploadConfig } from "@/fileUpload/fileUploadDomain";
import { DocumentLanguage, LocalizedText } from "@/common/common";
import { openSendNoticeDialogAction } from "@/dbot/summary/summaryDomain";
import { formatDateAndTime } from "@/i18n/dateFormat";
import { Language } from "@/i18n/languages";
import { DocumentType } from "@/common/documents";
import {
  BooleanFieldModel,
  LocalDateFieldModel,
  RadioFieldModel,
  requiredOfficialDocumentLanguageFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";

export class NoticeToGeneralMeetingForm implements WithInitialization {
  readonly type = DocumentType.notice_general_meeting;
  readonly allBoardMembers: string[] = [];
  readonly dispatch: DbotEventDispatcher;
  readonly context: DocumentContext<NoticeToGeneralMeeting>;

  publishArticlesOnCompanysWeb?: string;
  companyPublishedDocumentsRegardingResolutions: BooleanFieldModel;
  companyPublishedDocumentsRegardingResolutionsAddress?: string;
  dateNoticeGeneralMeeting: LocalDateFieldModel;
  noticeGeneralMeetingSender?: string;
  chairperson?: CompanyRoleIdWithName;
  meetingFormInlineAddress?: string;
  articlesPublishedOnTheAddress?: string;
  repairTextDeadlineGeneralMeeting: BooleanFieldModel;

  electronicParticipation: RadioFieldModel<GeneralMeetingElectronicParticipation>;
  electronicParticipationLink: StringFieldModel;

  documentLanguage: RadioFieldModel<DocumentLanguage>;
  decisionTextModels: DecisionTextModel[];
  additionalDetailsTextModel: LocalizedTextEditorModel;
  additionalDetailsTexts: LocalizedText[]; // initial value
  displayElectronicGMDialog = false;

  constructor(dispatch: DbotEventDispatcher, context: DocumentContext<NoticeToGeneralMeeting>) {
    makeAutoObservable(this, {}, { autoBind: true });
    const data = context.documentData;
    this.dispatch = dispatch;
    this.context = context;
    this.allBoardMembers = sortedBoard(this.context.initialState.roles).map(getFullName);
    this.chairperson = data.chairperson ?? getChairperson(this.context.initialState.roles);
    this.publishArticlesOnCompanysWeb = data.publishArticlesOnCompanysWeb;
    this.companyPublishedDocumentsRegardingResolutions = new BooleanFieldModel({
      value: data.companyPublishedDocumentsRegardingResolutions,
      required: true,
    });
    this.companyPublishedDocumentsRegardingResolutionsAddress =
      data.companyPublishedDocumentsRegardingResolutionsAddress;

    const defaultDates = defaultGeneralMeetingDates();
    this.dateNoticeGeneralMeeting = new LocalDateFieldModel({
      value: data.dateNoticeGeneralMeeting ?? defaultDates.notice,
    });

    this.noticeGeneralMeetingSender = data.noticeGeneralMeetingSender ?? this.chairperson?.name;
    this.meetingFormInlineAddress = data.meetingFormInlineAddress;
    this.articlesPublishedOnTheAddress = data.articlesPublishedOnTheAddress;
    this.repairTextDeadlineGeneralMeeting = new BooleanFieldModel({
      value: data.repairTextDeadlineGeneralMeeting,
      required: true,
    });

    this.electronicParticipation = createElectronicParticipationField(data.electronicParticipation);
    this.electronicParticipationLink = new StringFieldModel({
      value: data.electronicParticipationLink,
    });

    this.documentLanguage = requiredOfficialDocumentLanguageFieldModel({
      orgNo: this.context.initialState.company.organizationnumber,
      value: mapLocalesToDocumentLanguage(data?.documentLanguage),
    });

    this.additionalDetailsTexts = data?.additionalDetailsTexts ?? [];

    this.additionalDetailsTextModel = new LocalizedTextEditorModel({
      texts: data?.additionalDetailsTexts,
      isReadOnly: () => this.isLocked,
      label: { key: "documents.notice.description" },
      aiClient: context.aiClient,
    });
    this.decisionTextModels = mapLocalizedDecisionTextsToModel(
      context,
      dispatch,
      DocumentType.notice_general_meeting,
      this.documentLanguage.value,
    );
  }

  get initialState() {
    return this.context.initialState;
  }

  get shareholderCount() {
    return shareholdersWithShares(
      this.initialState.shareholders,
      this.initialState.sharesForShareholders,
    ).length;
  }

  get isSent() {
    return this.context.documentStatuses.some((d) => d.documentId === this.documentId && d.sentAt);
  }

  init(): CleanupFunction {
    const cleanupFunctions = [
      autorun(() => this.handleLanguageChanges()),
      autorun(() => {
        if (!this.isTooShortNotice && this.repairTextDeadlineGeneralMeeting.value) {
          // Reset the repairTextDeadlineGeneralMeeting flag when the isTooShortNotice changes to false
          runInAction(() => this.repairTextDeadlineGeneralMeeting.setValue(false));
        }
      }),
    ];

    return () => {
      cleanupFunctions.forEach((f) => f());
    };
  }

  toggleConductElectronicGeneralMeeting() {
    const newValue = !this.context.generalMeetingFields.conductElectronicGeneralMeeting.value;
    this.context.generalMeetingFields.conductElectronicGeneralMeeting.setValue(newValue);
    if (newValue) {
      if (!this.hasLiveGeneralMeetingFeature) {
        this.displayElectronicGMDialog = true;
      }
      this.dispatch({ type: "dbot/add-documents", documentTypes: [DocumentType.general_meeting] });
    }
  }

  get hasLiveGeneralMeetingFeature() {
    return this.context.featureToggles.liveGM ?? false;
  }

  toggleDisplayElectronicGMDialog() {
    this.displayElectronicGMDialog = !this.displayElectronicGMDialog;
  }

  unOrderElectronicGeneralMeeting() {
    this.toggleDisplayElectronicGMDialog();
    this.toggleConductElectronicGeneralMeeting();
  }

  orderElectronicGeneralMeeting() {
    this.dispatch({
      type: "order-live-general-meeting",
      orgNo: this.context.initialState.company.organizationnumber,
      projectId: this.context.projectId,
      generalMeetingDate: formatDateAndTime(
        this.locale,
        this.context.generalMeetingFields.dateGeneralMeeting.inputValue,
        this.context.generalMeetingFields.timeGeneralMeeting.inputValue,
      ),
      onSuccess: this.toggleDisplayElectronicGMDialog,
      onFailure: this.toggleDisplayElectronicGMDialog,
    });
  }

  get locale(): Language {
    return this.context.locale;
  }

  get currentDecisions(): DecisionItem[] {
    return this.context.currentDecisions.filter((d) => d.includeInGeneralMeeting);
  }

  get isTooShortNotice(): boolean {
    return isTooShortNotice(
      this.dateNoticeGeneralMeeting.toISOString(),
      this.context.generalMeetingFields.dateGeneralMeeting.toISOString(),
    );
  }

  get isLocked(): boolean {
    return this.context.isProjectLocked;
  }

  get fileUploadConfig(): FileUploadConfig {
    return this.context.fileUploadConfig;
  }

  get simplifiedGeneralMeeting(): boolean {
    return this.context.generalMeetingFields.isSimplifiedGeneralMeeting.value ?? true;
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/decision-added":
        this.initDecisionTextModel(event.decisionId);
        break;
    }
  }
  get openGeneralMeetingReleaseDialog() {
    return this.dispatch([
      {
        type: "showGeneralMeetingAnnouncementDialog",
      },
    ]);
  }
  setSelectNoticeGeneralMeetingSender(value: string) {
    this.noticeGeneralMeetingSender = value;
  }

  setCompanyPublishedDocumentsRegardingResolutionsAddress(value: string) {
    this.companyPublishedDocumentsRegardingResolutionsAddress = value;
  }

  get documentId() {
    return this.context.documentId;
  }

  get documentItem(): DocumentItem {
    return this.context.currentDocuments.find((d) => d.documentId === this.documentId)!;
  }

  get documentStatus(): ProjectDocumentStatus | undefined {
    return this.context.documentStatuses.find((s) => s.documentId === this.documentId);
  }

  get meetingForm(): RadioFieldModel<GeneralMeetingForm> {
    return this.context.generalMeetingFields.meetingForm;
  }

  get generalMeetingType(): RadioFieldModel<GeneralMeetingType> {
    return this.context.generalMeetingFields.generalMeetingType;
  }

  get openSendNoticeDialogAction() {
    return openSendNoticeDialogAction({
      document: this.documentItem,
      isSendingAvailable: !this.documentStatus?.sentAt,
      isBatchProject: this.context.isBatchProject,
    });
  }

  initDecisionTextModel(decisionId: string) {
    const decisionTextModel = this.decisionTextModels.find((d) => d.decisionId === decisionId);
    if (decisionTextModel === undefined) {
      const decision = this.context.currentDecisions.find((d) => d.decisionId === decisionId);
      const documentLanguages = mapDocumentLanguageToLocales(this.documentLanguage.value);
      if (decision) {
        const model = mapDecisionTextModel(
          this.dispatch,
          decision,
          DocumentType.notice_general_meeting,
          this.context,
          documentLanguages,
        );
        this.decisionTextModels.push(model);
      }
    }
  }

  private handleLanguageChanges() {
    const documentLanguages = mapDocumentLanguageToLocales(this.documentLanguage.value);
    if (isLanguageChanged(documentLanguages, this.additionalDetailsTextModel.textModels)) {
      runInAction(() => {
        this.additionalDetailsTextModel.onLanguageChange(
          documentLanguages,
          this.additionalDetailsTexts,
        );
        this.decisionTextModels = mapLocalizedDecisionTextsToModel(
          this.context,
          this.dispatch,
          DocumentType.notice_general_meeting,
          this.documentLanguage.value,
        );
      });
    }
  }
}

export function initNewForm(
  dispatch: DbotEventDispatcher,
  context: DocumentContext<NoticeToGeneralMeeting>,
): NoticeToGeneralMeetingForm {
  return new NoticeToGeneralMeetingForm(dispatch, context);
}

export function handleEvent(form: NoticeToGeneralMeetingForm, event: DbotEvent) {
  form.handleEvent(event);
}

export function applyForm(form: NoticeToGeneralMeetingForm): NoticeToGeneralMeeting {
  return {
    type: DocumentType.notice_general_meeting,
    publishArticlesOnCompanysWeb: form.publishArticlesOnCompanysWeb,
    companyPublishedDocumentsRegardingResolutions:
      form.companyPublishedDocumentsRegardingResolutions.value ?? false,
    companyPublishedDocumentsRegardingResolutionsAddress:
      form.companyPublishedDocumentsRegardingResolutionsAddress,
    dateNoticeGeneralMeeting: form.dateNoticeGeneralMeeting.toISOString(),
    noticeGeneralMeetingSender: form.noticeGeneralMeetingSender,
    chairperson: form.chairperson,
    meetingFormInlineAddress: form.meetingFormInlineAddress,
    articlesPublishedOnTheAddress: form.articlesPublishedOnTheAddress,
    repairTextDeadlineGeneralMeeting:
      !form.simplifiedGeneralMeeting && (form.repairTextDeadlineGeneralMeeting.value ?? false),
    electronicParticipation: form.electronicParticipation.value,
    electronicParticipationLink: form.electronicParticipationLink.validatedValue,
    documentLanguage: mapDocumentLanguageToLocales(form.documentLanguage.value),
    additionalDetailsTexts: form.additionalDetailsTextModel.localizedTexts,
  };
}

export function supportedLocalesToPowerOfAttorneyLanguage(
  locales?: Language[],
): PowerOfAttorneyLanguage | undefined {
  if (!locales) {
    return undefined;
  }
  if (locales.includes(Language.NO) && locales.includes(Language.EN)) {
    return "both";
  }
  if (locales.includes(Language.NO)) {
    return "no";
  }
  if (locales.includes(Language.EN)) {
    return "en";
  }
}

export enum NoticeToGeneralMeetingCustomPanel {
  meetingDetails = "meetingDetails",
}

export const getNoticeToGeneralMeetingDetailsPanel = (
  form: NoticeToGeneralMeetingForm,
): DetailsPanel => ({
  type: "custom",
  id: NoticeToGeneralMeetingCustomPanel.meetingDetails,
  heading: { key: "dbot.meeting-details" },
  icon: "calendar",
  documentId: form.documentId,
  label: {
    translated: formatDateAndTime(
      form.locale,
      form.context.generalMeetingFields.dateGeneralMeeting.validatedValue,
      form.context.generalMeetingFields.timeGeneralMeeting.validatedValue,
    ),
  },
});

export function getNoticeToGeneralMeetingCustomPanels(
  form: NoticeToGeneralMeetingForm,
): DetailsPanel[] {
  return [getNoticeToGeneralMeetingDetailsPanel(form)];
}
